import { useState } from 'react';
import type { Path } from 'slate';
import type { SlateEditor } from 'platejs';

import { getEmbedNode, insertEmbed, replaceEmbed, IEmbedValue } from './utils';

/**
 * Shared open/prefill/submit state for the embed insert-or-edit dialog,
 * used by both the toolbar button and the block-menu "edit" action.
 *
 * The target node's path is captured once when the dialog opens and reused
 * on submit, rather than re-deriving it from the editor's selection at
 * submit time — the selection could otherwise have moved while the modal
 * was open, silently editing a different embed than the one shown.
 */
export const useEmbedDialogState = (editor: SlateEditor) => {
    const [open, setOpen] = useState(false);
    const [initialValue, setInitialValue] = useState<string | undefined>();
    const [targetPath, setTargetPath] = useState<Path | undefined>();

    const onTrigger = () => {
        const embedNode = getEmbedNode(editor);
        const value = embedNode
            ? (embedNode[0] as { url?: string; html?: string })
            : undefined;

        setInitialValue(value?.url ?? value?.html);
        setTargetPath(embedNode?.[1]);
        setOpen(true);
    };

    const onClose = () => setOpen(false);

    const onSubmit = (value: IEmbedValue) => {
        if (targetPath) {
            replaceEmbed(editor, targetPath, value);
        } else {
            insertEmbed(editor, value);
        }

        setOpen(false);
    };

    return { open, initialValue, onTrigger, onClose, onSubmit };
};
